import { useEffect, useRef, useState } from 'react'
import isEqual from 'lodash-es/isEqual'

import { SAVE_DRAFT_TIMEOUT } from '../../../../constants/draft'
import { useThrottledState } from '../../../../hooks'
import { updateStateValue } from '../../../../utils/react'
import { useDraftStorage } from '../useDraftStorage'

export function useDraftProvider({ draftId }) {
  const draftsStorage = useDraftStorage()

  const [draft, setDraft] = useState()

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft(undefined)

    if (draftId != null) {
      draftsStorage.getItem(draftId).then(setDraft)
    }
  }, [draftId, draftsStorage])

  // useThrottledState stores commitCallback in a ref, so passing a fresh
  // closure here each render is fine — it always reads latest draftId and
  // storage when it eventually fires.
  async function saveDraft(value) {
    const saved = await draftsStorage.setItem(draftId, value)

    setDraft(saved)
  }

  const [scheduleSaveDraft, getLatestDraft, cancelScheduledSaveDraft] =
    useThrottledState(saveDraft)

  useEffect(() => {
    // Drop any value scheduled for the previous draftId so it can't commit
    // into the new draftId's storage entity after the switch.
    cancelScheduledSaveDraft()
  }, [cancelScheduledSaveDraft, draftId])

  const draftRef = useRef(draft)

  useEffect(() => {
    draftRef.current = draft
  }, [draft])

  function updateDraft(nextDraftAction, timeout = SAVE_DRAFT_TIMEOUT) {
    const latest = getLatestDraft()
    const current = latest === undefined ? draftRef.current : latest
    const nextDraft = updateStateValue(nextDraftAction, current)
    const isChanged = !isEqual(nextDraft ?? null, current ?? null)

    if (draftId != null && (isChanged || timeout === 0)) {
      scheduleSaveDraft(nextDraft, timeout)
    }
  }

  return {
    draft,
    draftId,
    getLatestDraft,
    setDraft: updateDraft,
  }
}
